import { useMemo } from "react"
import { useRouterState } from "@tanstack/react-router"
import {
  Brain,
  ChartLine,
  FlowArrow,
  Gear,
  ListChecks,
  Package,
  Scissors,
  Sparkle,
  Target,
  TShirt,
} from "@phosphor-icons/react"

import { formatColumnName, ProductTableColumn } from "~/utils/productTableUtils"
import { CollectionTableColumn } from "~/constants/collections"
import { SalesMetricColumns, TrimMetricsColumns } from "~/utils/demandDictionary"
import { NoosOrderSuggestionsColumn, SupplyTableColumn } from "~/constants/supplyPlan"
import { NpiListColumns } from "~/constants/nearestNeighbors"
import ColumnChooserCore from "~/components/planning/columnChooser/ColumnChooserCore"
import { useChooserColumns } from "~/components/planning/columnChooser/useChooserColumns"
import { useColumnAvailability } from "./utils/columnAvailability"
import { columnNaming, TableColumnGroups, type NamedColumnId } from "./utils/columnUtils"

type ColumnChooserProps = {
  columnIds: string[]
  displayedColumnIds: string[]
  setDisplayedColumnIds: (a: string[]) => void
  defaultColumnIds: string[]
  /** Per-table overrides for a column's chooser label, when it depends on runtime state. */
  columnTitleOverrides?: Record<string, string>
  /** Labels for columns without a `columnNaming` entry, e.g. an extra column's `headerName`. */
  fallbackColumnTitles?: Record<string, string>
  tableId: string
}

export { TableColumnGroups }

/**
 * Curated set of columns the column chooser exposes. The naming, tooltip, grouping and search
 * keywords for each id are resolved from {@link columnNaming} so labels live in a single place.
 */
const COLUMN_CHOOSER_IDS: NamedColumnId[] = [
  ProductTableColumn.ProductName,
  ProductTableColumn.Product,
  ProductTableColumn.Variant,
  ProductTableColumn.ProductId,
  ProductTableColumn.VariantId,
  ProductTableColumn.VariantName,
  ProductTableColumn.SKU,
  ProductTableColumn.Planner,
  ProductTableColumn.Flags,
  ProductTableColumn.Brand,
  ProductTableColumn.Category,
  ProductTableColumn.Gender,
  ProductTableColumn.Tags,
  ProductTableColumn.Color,
  ProductTableColumn.BaseColor,
  ProductTableColumn.Size,
  ProductTableColumn.SizeDimension1,
  ProductTableColumn.SizeDimension2,
  ProductTableColumn.SizeSplitConfidence,
  ProductTableColumn.SupplierSku,
  ProductTableColumn.Vendor,
  ProductTableColumn.PreferredVendor,
  ProductTableColumn.ProductType,
  ProductTableColumn.AvailabilityToday,
  ProductTableColumn.LocationAvailability,
  ProductTableColumn.LocationMinimumStockLevel,
  ProductTableColumn.OverstockQuantity,
  ProductTableColumn.OverstockValue,
  ProductTableColumn.SuggestedOrder,
  ProductTableColumn.ExistingPO,
  ProductTableColumn.StockLevel,
  ProductTableColumn.ReservedStockLevel,
  ProductTableColumn.AvailableToSell,
  ProductTableColumn.IncomingStock,
  ProductTableColumn.AvailabilityWithinLeadTime,
  ProductTableColumn.OptimalOrderDate,
  ProductTableColumn.SafetyStockOutDate,
  ProductTableColumn.OutOfStockDate,
  ProductTableColumn.StockStatus,
  ProductTableColumn.InventoryValue,
  ProductTableColumn.CostPrice,
  ProductTableColumn.RecommendedRetailPrice,
  ProductTableColumn.WholesalePrice,
  ProductTableColumn.SellThroughRate,
  ProductTableColumn.InventoryTurn,
  ProductTableColumn.GMROI,
  ProductTableColumn.Sales,
  ProductTableColumn.NetSales,
  ProductTableColumn.SalesLast30Days,
  ProductTableColumn.Profit,
  ProductTableColumn.PreOrders,
  TrimMetricsColumns.PreorderQuantity,
  ProductTableColumn.PreOrderFulfillment,
  ProductTableColumn.UnfulfillablePreorder,
  ProductTableColumn.AtRiskPreorder,
  ProductTableColumn.PreOrdersLastYear,
  ProductTableColumn.Revenue,
  ProductTableColumn.Forecast,
  ProductTableColumn.WeeksOnHand,
  ProductTableColumn.SalesGrowth,
  ProductTableColumn.ForecastedGrowth,
  ProductTableColumn.ForecastedGrowthAlert,
  ProductTableColumn.ForecastInfo,
  ProductTableColumn.ForecastSalesVsLy1m,
  ProductTableColumn.ForecastSalesVsLy3m,
  ProductTableColumn.ForecastSalesVsLy6m,
  ProductTableColumn.ForecastSalesVsLy12m,
  ProductTableColumn.ForecastedGrowthUnits,
  ProductTableColumn.ForecastWithoutKnownPreOrders,
  ProductTableColumn.LostSales,
  ProductTableColumn.SoldSince,
  ProductTableColumn.LeadTime,
  ProductTableColumn.ServiceLevel,
  ProductTableColumn.WeekCover,
  ProductTableColumn.PurchaseInterval,
  ProductTableColumn.MinimumOrderQuantity,
  ProductTableColumn.SecondaryMOQ,
  ProductTableColumn.Buffer,
  ProductTableColumn.SafetyStockValue,
  ProductTableColumn.ColliSize,
  CollectionTableColumn.BelongingToCollection,
  CollectionTableColumn.Out,
  CollectionTableColumn.ProjectedSales,
  CollectionTableColumn.Forecast,
  CollectionTableColumn.Sales,
  CollectionTableColumn.SalesAmount,
  CollectionTableColumn.PreOrders,
  CollectionTableColumn.SalesRisk,
  CollectionTableColumn.SalesRiskComparison,
  CollectionTableColumn.PreOrdersAmount,
  CollectionTableColumn.InboundPreOrders,
  CollectionTableColumn.PreOrdersWithoutInbound,
  CollectionTableColumn.FuturePreOrders,
  CollectionTableColumn.ExpectedReturns,
  CollectionTableColumn.ProjectedSalesComparison,
  CollectionTableColumn.ProjectedStockLevel,
  CollectionTableColumn.ProjectedInventoryValue,
  CollectionTableColumn.IncomingOrders,
  CollectionTableColumn.IncomingOrdersComparison,
  CollectionTableColumn.Budget,
  CollectionTableColumn.ForecastComparison,
  CollectionTableColumn.SalesComparison,
  CollectionTableColumn.PreOrdersComparison,
  CollectionTableColumn.InboundPreOrdersComparison,
  CollectionTableColumn.PreOrdersWithoutInboundComparison,
  ProductTableColumn.ForecastModel,
  ProductTableColumn.ABC,
  ProductTableColumn.LifecycleStatus,
  ProductTableColumn.Returns,
  ProductTableColumn.ReturnRate,
  ProductTableColumn.ReturnRateWholeHorizon,
  ProductTableColumn.ExpectedReturns,
  ProductTableColumn.Collection,
  ProductTableColumn.Adjustments,
  ProductTableColumn.Fabric,
  ProductTableColumn.ActivatedFrom,
  ProductTableColumn.ActivatedUntil,
  ProductTableColumn.PhasingOutFrom,
  ProductTableColumn.ForecastQuality,
  ProductTableColumn.Rules,
  CollectionTableColumn.ProjectedSalesWithoutPreOrders,
  SalesMetricColumns.Alerts,
  SalesMetricColumns.Baseline,
  SalesMetricColumns.BaselineMethod,
  SalesMetricColumns.BaselineMatchCount,
  SalesMetricColumns.BaselineMatchQuality,
  SalesMetricColumns.BaselineInfo,
  SalesMetricColumns.BaselinePreOrders,
  SalesMetricColumns.Type,
  SalesMetricColumns.Adjustments,
  SalesMetricColumns.AddedQuantityStockout,
  SalesMetricColumns.AddedQuantityMissing,
  SalesMetricColumns.AddedQuantityDiscount,
  SalesMetricColumns.PreorderChange,
  SalesMetricColumns.ExpectedPreorderChange,
  // SalesMetricColumns.CategoryTrend,
  // SalesMetricColumns.ColorTrend,
  // SalesMetricColumns.GenderTrend,
  SalesMetricColumns.RiskBuyForecast,
  SalesMetricColumns.RiskBuyForecastPct,
  SalesMetricColumns.RiskBuyForecastComparison,
  SalesMetricColumns.RiskBuyForecastComparisonPct,
  SalesMetricColumns.PreordersVsBaseline,
  SalesMetricColumns.PreordersVsBaselinePct,
  SalesMetricColumns.AdjustmentsPct,
  SalesMetricColumns.FinalForecast,
  TrimMetricsColumns.ExpectedPreorders,
  TrimMetricsColumns.CustomerCount,
  TrimMetricsColumns.CustomerCoverage,
  TrimMetricsColumns.AvgQuantityPerCustomer,
  TrimMetricsColumns.CustomerInterest,
  TrimMetricsColumns.Cannibalisation,
  TrimMetricsColumns.SourcingRisk,
  TrimMetricsColumns.TrimAction,
  SupplyTableColumn.AlreadyPurchasedQty,
  SupplyTableColumn.ForecastInPeriod,
  SupplyTableColumn.RiskStockQty,
  SupplyTableColumn.RiskStockPct,
  SupplyTableColumn.RecommendedRiskBuyPct,
  SupplyTableColumn.PlannedBuyTotal,
  SupplyTableColumn.ProjectedStockStartOfPeriod,
  SupplyTableColumn.ProjectedStockEndOfPeriod,
  SupplyTableColumn.SuggestedOrderInPeriod,
  SupplyTableColumn.TotalPurchaseQty,
  SupplyTableColumn.SimulatedAvailability,
  SupplyTableColumn.AvailableIncludingNextSeason,
  SupplyTableColumn.AvailabilityViolation,
  SupplyTableColumn.TotalDemand,
  // The fashion supply plan's per-warehouse date trio. Listed here so they read their
  // columnNaming titles and sit under Supply Planning instead of landing in "Other" as
  // formatColumnName("eta") = "Eta".
  SupplyTableColumn.ETA,
  SupplyTableColumn.ETD,
  SupplyTableColumn.OPD,
  ProductTableColumn.SalesWithoutPreOrders,
  ProductTableColumn.ForecastWithoutPreOrders,
  ProductTableColumn.GrowthWithoutPreOrders,
  ProductTableColumn.StockoutImputation,
  ProductTableColumn.MissingSalesImputation,
  NoosOrderSuggestionsColumn.OrderStatus,
  NoosOrderSuggestionsColumn.SuggestedQty,
  NoosOrderSuggestionsColumn.MoqSuggestedQty,
  NoosOrderSuggestionsColumn.OrderByDate,
  NoosOrderSuggestionsColumn.TriggerReasons,
  NoosOrderSuggestionsColumn.TriggerStockout,
  NoosOrderSuggestionsColumn.TriggerImpact,
  // NPI list only — filtered out of other tables' choosers by the `columnIds.includes` check.
  NpiListColumns.FirstYearSales,
  NpiListColumns.YearOneDemand,
  NpiListColumns.SimulatedDemand,
  NpiListColumns.EstimatedFirstYearSales,
  NpiListColumns.Info,
  NpiListColumns.EstimatedSeasonality,
  NpiListColumns.MatchScore,
  NpiListColumns.MatchCount,
  NpiListColumns.ManualMatch,
  NpiListColumns.Update,
  NpiListColumns.WeeksSinceActivation,
]

export function getTableColumns(): {
  id: NamedColumnId
  displayName: string
  description?: string
  group: TableColumnGroups
  keyWords?: string[]
}[] {
  return COLUMN_CHOOSER_IDS.map((id) => {
    const naming = columnNaming[id]
    return {
      id,
      displayName: naming.columnChooserTitle,
      description: naming.tooltip,
      group: naming.group ?? TableColumnGroups.Other,
      keyWords: naming.keyWords,
    }
  })
}

export function getGroupIcon(groupName: TableColumnGroups) {
  const iconSize = 16
  switch (groupName) {
    case TableColumnGroups.ProductInformation:
      return <TShirt size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.AvailabilityAndInventory:
      return <Package size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.SalesAndForecast:
      return <ChartLine size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.SalesPerformance:
      return <Target size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.OperationalMetrics:
      return <Gear size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.Collections:
      return <FlowArrow size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.ModelsAndAnalysis:
      return <Brain size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.ExternalData:
      return <Sparkle size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.ExternalCollectionData:
      return <TShirt size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.DemandMetrics:
      return <ChartLine size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.TrimMetrics:
      return <Scissors size={iconSize} className="text-fg-muted" />
    case TableColumnGroups.NewProductIntroduction:
      return <Sparkle size={iconSize} className="text-fg-muted" />
    default:
      return <ListChecks size={iconSize} className="text-fg-muted" />
  }
}

function getRouteExpandedGroups(pathName: string): string[] {
  if (pathName.includes("/forecasting")) return [TableColumnGroups.SalesAndForecast]
  if (pathName.includes("/inventory-overview") || pathName.includes("/availability")) {
    return [TableColumnGroups.AvailabilityAndInventory]
  }
  if (pathName.includes("/demand-planning")) {
    if (pathName.includes("/product-management")) {
      return [TableColumnGroups.ProductInformation, TableColumnGroups.OperationalMetrics]
    }
    return [TableColumnGroups.Collections, TableColumnGroups.ExternalCollectionData]
  }
  return []
}

export default function ColumnChooser({
  columnIds,
  displayedColumnIds,
  setDisplayedColumnIds,
  defaultColumnIds,
  columnTitleOverrides,
  fallbackColumnTitles,
  tableId,
}: ColumnChooserProps) {
  const { hiddenColumnIds } = useColumnAvailability()
  const pathName = useRouterState({ select: (state) => state.location.pathname })

  const curated = useMemo(() => getTableColumns(), [])
  const isHiddenOther = useMemo(() => {
    const hidden = new Set<string>(hiddenColumnIds)
    return (id: string) => hidden.has(id)
  }, [hiddenColumnIds])
  const titles = useMemo(
    () => ({
      overrides: (id: string) => columnTitleOverrides?.[id],
      fallbacks: fallbackColumnTitles,
      format: formatColumnName,
    }),
    [columnTitleOverrides, fallbackColumnTitles],
  )
  const columns = useChooserColumns({ columnIds, curated, titles, isHiddenOther })

  return (
    <ColumnChooserCore
      columns={columns}
      columnIds={columnIds}
      displayedColumnIds={displayedColumnIds}
      setDisplayedColumnIds={setDisplayedColumnIds}
      defaultColumnIds={defaultColumnIds}
      getGroupIcon={(group) => getGroupIcon(group as TableColumnGroups)}
      initialExpandedGroups={getRouteExpandedGroups(pathName)}
      tableId={tableId}
      triggerTestId="column-chooser-button"
    />
  )
}
